import { ALL_AUTO_ADD, AutoAddNewBidderSeats } from '@pubstack/common/src/bidder-rule'
import { BidderSeat } from '@pubstack/common/src/bidder-seat'
import { MediaType } from '@pubstack/common/src/media-types'
import { TextInput } from '@mantine/core'
import { FunctionComponent, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useBidderRule, useBidderRules, useCreateBidderRule, useUpdateBidderRule } from '~/api/bidder-rules.api'
import { useBidderSeats } from '~/api/bidder-seats.api'
import { useCatalog } from '~/api/catalog.api'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { Tooltip } from '~/components/Tooltip'
import { PageContent } from '~/components/PageContent'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { BidderRuleAutoAddSection } from './BidderRuleAutoAddSection'
import { BidderRuleSeatTable } from './BidderRuleSeatTable'
import { BIDDER_RULES_PATH } from './bidderRulesPath'
import { BIDDER_RULE_FORM_SECTIONS, SelectedBidderSeats, sectionAvailability, storedSectionsOf, toggleAllSeats, toggleMediaType, toggleSeat } from './bidderRuleSections'
import { useBidderRuleApiProduct, useBidderRuleCapabilities } from './useBidderRuleCapabilities'

export const BidderRuleEditPage: FunctionComponent = () => {
  const { bidderRuleId } = useParams()
  const navigate = useNavigate()
  const breadcrumbs = useBreadcrumbs()
  const capabilities = useBidderRuleCapabilities()
  const apiProduct = useBidderRuleApiProduct()

  // Both products' seats and catalogs: the rule spans all the sections, so it cannot be loaded
  // through one product's endpoint.
  const { data: admSeats } = useBidderSeats('adm')
  const { data: pbsSeats } = useBidderSeats('pbs')
  const { data: admCatalog } = useCatalog('adm')
  const { data: pbsCatalog } = useCatalog('pbs')

  const { data: existingRule, isLoading } = useBidderRule(apiProduct, bidderRuleId)
  const { data: allRules } = useBidderRules(apiProduct)

  const createBidderRule = useCreateBidderRule(apiProduct, { successToast: 'Bidder rule created', errorToast: 'Could not create the bidder rule' })
  const updateBidderRule = useUpdateBidderRule(apiProduct, bidderRuleId ?? '', { successToast: 'Bidder rule updated', errorToast: 'Could not update the bidder rule' })

  const [name, setName] = useState('')
  // A pristine new rule has an empty name, which is invalid — but the publisher has not got it wrong yet,
  // they have not started. The error is computed all along so Save stays disabled; only its display waits
  // for the field to have been used.
  const [nameTouched, setNameTouched] = useState(false)
  const [selected, setSelected] = useState<SelectedBidderSeats>([])
  // A new rule starts with auto-add on; hydration below overwrites it when editing one.
  const [autoAdd, setAutoAdd] = useState<AutoAddNewBidderSeats>({ ...ALL_AUTO_ADD })

  // Hydrated during render rather than from an effect, which would setState in a cascading pass.
  // Keyed on the rule's id so a later refetch of the same rule cannot wipe unsaved edits.
  const [hydratedRuleId, setHydratedRuleId] = useState<string | undefined>(undefined)
  if (existingRule && hydratedRuleId !== existingRule.id) {
    setHydratedRuleId(existingRule.id)
    setName(existingRule.name)
    setSelected(existingRule.selectedBidderSeats)
    setAutoAdd(existingRule.autoAddNewBidderSeats)
  }

  const seats: BidderSeat[] = useMemo(() => [...admSeats, ...pbsSeats], [admSeats, pbsSeats])
  const catalogBidders = useMemo(() => [...admCatalog, ...pbsCatalog], [admCatalog, pbsCatalog])

  /* Saving a rule is owner-only at the API: a rule is shared, and every stack pointing at it moves when
     it does. The form still opens and reads — which seats a rule grants is worth seeing either way. */
  const { canEdit, disabledReason } = useScopePermissions()

  // The API keeps a default's name whatever the payload says, so the field is read-only here and its
  // name never has to clear the empty/duplicate checks the publisher cannot act on.
  const isDefault = existingRule?.isDefault ?? false

  const nameError = useMemo(() => {
    if (isDefault) return undefined
    if (!name.trim()) return 'A rule needs a name'
    return allRules.some((rule) => rule.id !== bidderRuleId && rule.name.trim() === name.trim()) ? 'That name is already used' : undefined
  }, [isDefault, name, allRules, bidderRuleId])

  // Guards the double-click: the server's name-uniqueness check is check-then-insert, so two
  // concurrent creates would both pass it and persist duplicate rules.
  const isSaving = createBidderRule.isPending || updateBidderRule.isPending

  const onSave = async () => {
    if (nameError || isSaving) return
    // `selected` is edited in place throughout, never rebuilt from the rendered sections, so seats
    // in a blocked section survive the save untouched.
    // Carry the stored default status through an edit. Hardcoding `false` here strips it from the
    // scope's default rule, and a scope with no default rule breaks seat auto-add and the deployment
    // center.
    const payload = {
      name: name.trim(),
      selectedBidderSeats: selected,
      autoAddNewBidderSeats: autoAdd,
      isDefault: existingRule?.isDefault ?? false,
      defaultFor: existingRule?.defaultFor ?? [],
    }
    try {
      if (bidderRuleId) {
        await updateBidderRule.mutateAsync({ ...payload, id: bidderRuleId })
      } else {
        await createBidderRule.mutateAsync(payload)
      }
    } catch {
      // The mutation's errorToast has already said what went wrong; staying on the page keeps the edits.
      return
    }
    navigate(BIDDER_RULES_PATH)
  }

  return (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        <Button variant="tertiary" onClick={() => navigate(BIDDER_RULES_PATH)} trackingLabel="common-bidder-rules-back">
          Back
        </Button>
      }
    >
      {/* The rule form cards up like the other rule forms — white panel, lilac edge, title over a rule,
          actions in the footer. Written in Tailwind rather than reusing the emotion `ContentCard`, which
          ADR-0031 deprecates; the tokens are the same, so the two read as one family on screen.

          `mx-auto` because the width is capped: past 1180px the card stopped growing but stayed against
          the left edge, leaving the form pinned to one side of an increasingly empty page. */}
      <div className="mx-auto max-w-[1180px] rounded border-l-[6px] border-l-lilac bg-white p-5">
        <h1 className="pb-5 text-2xl">{bidderRuleId ? `Edit: ${existingRule?.name ?? ''}` : 'New bidder rule'}</h1>
        <div className="border-t border-platinum" />

        <div className="flex flex-col gap-8 py-6">
          <section className="flex flex-col gap-2">
            <h2 className="font-medium">Identification</h2>
            <TextInput
              placeholder="Name"
              value={name}
              onChange={(event) => {
                setNameTouched(true)
                setName(event.currentTarget.value)
              }}
              onBlur={() => setNameTouched(true)}
              error={nameTouched && !isDefault ? nameError : undefined}
              disabled={isDefault}
              description={isDefault ? 'The default rule keeps its name.' : undefined}
              className="max-w-md"
            />
          </section>

          <BidderRuleAutoAddSection
            sections={BIDDER_RULE_FORM_SECTIONS.filter((section) => sectionAvailability(section, capabilities) === 'active')}
            autoAdd={autoAdd}
            // Both of the server-side section's flags, since it is one list and therefore one setting —
            // leaving the deprecated flag without a control would strand it wherever it happened to sit.
            onToggleAutoAdd={(section, enabled) => setAutoAdd((current) => ({ ...current, ...Object.fromEntries(storedSectionsOf(section).map((stored) => [stored, enabled])) }))}
          />

          <BidderRuleSeatTable
            seats={seats}
            catalogBidders={catalogBidders}
            selected={selected}
            capabilities={capabilities}
            onToggleSeat={(seat, enabled) => setSelected((current) => toggleSeat(seat, enabled, current, catalogBidders))}
            onToggleMediaType={(seat: BidderSeat, mediaType: MediaType) => setSelected((current) => toggleMediaType(seat, mediaType, current, catalogBidders))}
            onToggleAllSeats={(seatsToToggle, enabled) => setSelected((current) => toggleAllSeats(seatsToToggle, enabled, current, catalogBidders))}
            // No `bidderRuleId` is the new-rule route, and a new rule has nothing enabled yet.
            initialTypeFilter={bidderRuleId ? 'enabled' : 'all'}
          />
        </div>

        <div className="flex justify-end gap-4 border-t border-platinum pt-4">
          <Button variant="tertiary" onClick={() => navigate(BIDDER_RULES_PATH)} trackingLabel="common-bidder-rules-cancel">
            Cancel
          </Button>
          <Tooltip title={disabledReason} disabled={canEdit}>
            <Button variant="primary" onClick={onSave} disabled={!canEdit || !!nameError || isLoading || isSaving} trackingLabel="common-bidder-rules-save">
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Tooltip>
        </div>
      </div>
    </PageContent>
  )
}
